// Bilan de saison (D-143, maquettes b1 et b2) : logique pure, testée (bilan.test.ts). Les écrans : src/ui/bilan.ts.
//  * 4 blocs : le projet (continuer, changer d'équipe, arrêter, je ne sais pas), les envies (coacher, arbitrer, la table de
//    marque, bénévole, le bureau ; pour un mineur, seulement ce que le club ouvre aux jeunes, D-103), sa saison (ambiance,
//    encadrement, créneaux : les 5 visages), un mot ; « Et toi ? » pour le parent.
//  * Chaque bloc dit qui le voit. Accords : agree() d'après le genre de la personne (D-008 : sinon masculin générique).
import { agree, type TeamGender } from './labels';
import { expectedCategory } from './import';

export type Project = 'continue' | 'change' | 'stop' | 'unsure';
export type Help = 'coach' | 'referee' | 'otm' | 'volunteer' | 'board';
export const HELPS: Help[] = ['coach', 'referee', 'otm', 'volunteer', 'board'];
/** Pour un mineur (D-103) : arbitrer et tenir la table des matchs de jeunes, aider un coach des plus jeunes. */
export const MINOR_HELPS: Help[] = ['referee', 'otm', 'coach'];

export interface HelpText { title: string; sub: string; icon: string }
/** Les envies, telles que les voit un adulte qui répond pour lui-même. */
export const HELP_SELF: Record<Help, HelpText> = {
  coach: { title: 'Coacher', sub: 'aide-coach d’une équipe de jeunes', icon: 'ti-whistle' },
  referee: { title: 'Arbitrer', sub: 'arbitre club, matchs de jeunes', icon: 'ti-flag' },
  otm: { title: 'Table de marque', sub: 'OTM : marque, chrono, 24 s', icon: 'ti-chrono' },
  volunteer: { title: 'Bénévole', sub: 'buvette, accueil, déplacements', icon: 'ti-heart' },
  board: { title: 'Rejoindre le bureau', sub: 'trésorerie, secrétariat, projets', icon: 'ti-shield' },
};
/** Pour un mineur : seulement ce que le club ouvre aux jeunes, toujours avec un adulte à côté. */
export const HELP_MINOR: Partial<Record<Help, HelpText>> = {
  referee: { title: 'Arbitrer', sub: 'les matchs des plus jeunes', icon: 'ti-flag' },
  otm: { title: 'Table de marque', sub: 'aux matchs de jeunes', icon: 'ti-chrono' },
  coach: { title: 'Aider un coach', sub: 'des U9 ou des U11', icon: 'ti-whistle' },
};
/** « Et toi, Claire ? » : les envies du parent. */
export const HELP_PARENT: Record<Help, HelpText> = {
  otm: { title: 'Table de marque', sub: 'OTM, le club te forme', icon: 'ti-chrono' },
  volunteer: { title: 'Bénévole', sub: 'buvette, déplacements', icon: 'ti-heart' },
  coach: { title: 'Coacher', sub: 'une équipe de jeunes', icon: 'ti-whistle' },
  board: { title: 'Le bureau', sub: 'trésorerie, projets', icon: 'ti-shield' },
  referee: { title: 'Arbitrer', sub: 'arbitre club', icon: 'ti-flag' },
};
/** L'ordre des envies d'un parent (la maquette b1c : table, bénévole, coacher, le bureau). */
export const PARENT_HELPS: Help[] = ['otm', 'volunteer', 'coach', 'board'];

/** Le libellé court d'une envie (le bureau, « Futurs officiels »). */
export const HELP_GROUP: Record<Help, string> = { coach: 'Coachs', referee: 'Arbitres club', otm: 'Table de marque', volunteer: 'Bénévoles', board: 'Le bureau' };

export interface ProjectText { title: string; sub: string }
/** Les 4 projets. `self` : la personne répond (« Je continue ») ; sinon son parent (« Elle continue »). */
export function projectTexts(o: { self: boolean; g: TeamGender; team: string | null; coach: string | null }): Record<Project, ProjectText> {
  const coach = o.coach ?? 'le coach';
  if (o.self) return {
    continue: { title: 'Je continue', sub: o.team ? `en ${o.team}${o.coach ? `, avec ${o.coach}` : ''}` : 'au club' },
    change: { title: 'Je veux changer d’équipe', sub: `tu choisis laquelle, ${coach} est prévenu` },
    stop: { title: 'J’arrête', sub: 'tu peux dire pourquoi (déménagement, études, blessure…)' },
    unsure: { title: 'Je ne sais pas encore', sub: 'on te redemandera mi-juin' },
  };
  const il = agree(o.g, 'Il', 'Elle');
  return {
    continue: { title: `${il} continue`, sub: o.team ? `en ${o.team}` : 'au club' },
    change: { title: `${il} veut changer d’équipe`, sub: 'ou de club : dis-nous' },
    stop: { title: `${il} arrête`, sub: 'tu peux dire pourquoi' },
    unsure: { title: 'On ne sait pas encore', sub: 'on vous redemandera mi-juin' },
  };
}

/** La saison prochaine d'un nom de saison (« 2026-2027 » → « 2027-2028 »). */
export const nextSeason = (season: string): string => { const y = Number(season.slice(0, 4)) + 1; return `${y}-${y + 1}`; };
/** La catégorie de la saison prochaine, comme l'import FBI (âge en fin de saison) : « U15 ». */
export const nextCategory = (birthIso: string | null, season: string): string | null =>
  (birthIso ? expectedCategory(birthIso, Number(nextSeason(season).slice(5, 9))) : null);

/** Les 3 axes de sa saison. */
export const AXES: { key: 'ambiance' | 'coaching' | 'slots'; title: string; sub: string }[] = [
  { key: 'ambiance', title: 'L’ambiance', sub: 'dans l’équipe' },
  { key: 'coaching', title: 'L’encadrement', sub: 'coachs, séances' },
  { key: 'slots', title: 'Les créneaux', sub: 'jours, horaires, salles' },
];

/** Avancement du questionnaire : les 4 blocs remplis (le mot est facultatif : compté dès que le reste l'est). */
export function progress(a: { project: Project | null; helpsSeen: boolean; ambiance: number | null; coaching: number | null; slots: number | null; word: string | null }): boolean[] {
  const season = a.ambiance != null && a.coaching != null && a.slots != null;
  return [a.project != null, a.helpsSeen, season, !!a.word?.trim()];
}

const DAYS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
/** « 9 mots parlent du mercredi » : le jour le plus cité dans les mots, s'il revient au moins 3 fois. */
export function dayMentions(words: readonly (string | null)[]): { day: string; n: number } | null {
  let best: { day: string; n: number } | null = null;
  for (const day of DAYS) {
    const re = new RegExp(`\\b${day}s?\\b`, 'i');
    const n = words.filter(w => !!w && re.test(w)).length;
    if (n >= 3 && (!best || n > best.n)) best = { day, n };
  }
  return best;
}

/** Les comptes d'une équipe (prévision) : « 11 continuent · 1 change · 1 hésite · 1 arrête · 3 sans réponse ». */
export interface TeamCount { n: number; go: number; ch: number; hes: number; out: number; na: number }
export function countLine(c: TeamCount): string {
  const p = (k: number, one: string, many: string) => (k ? `${k} ${k > 1 ? many : one}` : '');
  return [p(c.go, 'continue', 'continuent'), p(c.ch, 'change', 'changent'), p(c.hes, 'hésite', 'hésitent'), p(c.out, 'arrête', 'arrêtent'),
    c.na ? `${c.na} sans réponse` : ''].filter(Boolean).join(' · ');
}
/** Les sûrs (continuent, changent d'équipe), et jusqu'où ça peut monter (+ ceux qui hésitent, + les sans réponse). */
export function forecast(teams: readonly TeamCount[]): { total: number; answered: number; sure: number; hes: number; na: number; out: number; max: number } {
  const s = (k: keyof TeamCount) => teams.reduce((n, t) => n + t[k], 0);
  const sure = s('go') + s('ch');
  return { total: s('n'), answered: s('n') - s('na'), sure, hes: s('hes'), na: s('na'), out: s('out'), max: sure + s('hes') + s('na') };
}
